import appendQuery from 'append-query';
import fetch from 'cross-fetch';

import { clearMaintenance, setMaintenance } from '../redux/modules/maintenance';
import LocalStorageService from '../services/local-storage-service';

import { createToast } from './toastUtil';

const defaultHeaders = {
  'Content-Type': 'application/json',
  Accept: 'application/json; text/plain; text/xml'
};

/**
 * getAuthHeaders
 *
 * Resolves the Authorization header from localStorage at CALL time rather
 * than at module-import time. `portal-api-token` can be (re)written after
 * the app has already booted — e.g. by the email/OTP verification flow,
 * which navigates via `history.push` and never triggers a full page reload —
 * so callers must not cache the token in a module-scope constant. Every api/
 * module should pass this function (not a pre-built headers object) as the
 * `headers` option to `createBaseApi` (DEV-3087).
 *
 * When there is no token we omit the header entirely rather than sending the
 * literal `Bearer null`. The API reads the JWT as
 * `cookie ?? bearerToken() ?? ...` and then `AccessToken::fromJwt()` only
 * short-circuits on an EMPTY value — the non-empty string "null" is parsed as
 * a JWT, fails, and hard-throws `AuthenticationException` (401) instead of
 * taking the graceful "no token" path. That is invisible in Chrome, which
 * accepts the `Secure` JWT cookie on http://localhost and therefore never
 * consults the Bearer header, but breaks Safari, which rejects `Secure`
 * cookies over plain HTTP and so falls through to it on every request.
 *
 * @returns {{ Authorization?: string }}
 */
export function getAuthHeaders() {
  const token = LocalStorageService.retrieve('portal-api-token');

  if (!token) {
    return {};
  }

  return { Authorization: `Bearer ${token}` };
}

/**
 * Module-level singleton that tracks whether a maintenance toast has already
 * been shown in the current browser session.  This prevents the toast from
 * firing on every subsequent request while maintenance is active.
 */
let maintenanceToastShown = false;

/**
 * getStore
 *
 * Lazily resolves the Redux store to avoid a circular dependency at module
 * load time (store → reducer → this file).
 */
let cachedStore = null;
const getStore = () => {
  if (!cachedStore) {
    // eslint-disable-next-line global-require
    cachedStore = require('../redux/middleware').default;
  }

  return cachedStore;
};

/**
 * handleMaintenanceResponse
 *
 * Called when a 503 with error_code === 'MAINTENANCE_MODE' is detected.
 * Dispatches the Redux state update and fires a one-time toast.
 *
 * @param {Object} body  - Parsed JSON body from the 503 response
 */
function handleMaintenanceResponse(body) {
  const store = getStore();

  store.dispatch(setMaintenance({
    message: body.message,
    starts_at: body.starts_at || null,
    ends_at: body.ends_at || null
  }));

  if (!maintenanceToastShown) {
    maintenanceToastShown = true;

    createToast({
      status: 'warning',
      title: 'PINS is currently undergoing maintenance',
      description: body.message || null,
      duration: 12000,
      isClosable: true
    });
  }
}

/**
 * handleSuccessfulResponsePostMaintenance
 *
 * When any successful response arrives and a maintenance window's ends_at has
 * already passed, clear the maintenance state from the store.
 */
function handleSuccessfulResponsePostMaintenance() {
  const store = getStore();
  const { maintenance } = store.getState();

  if (maintenance.active && maintenance.ends_at) {
    const endsAt = new Date(maintenance.ends_at);

    if (new Date() >= endsAt) {
      store.dispatch(clearMaintenance());
      maintenanceToastShown = false;
    }
  }
}

/**
 * createMaintenanceError
 *
 * Builds an Error instance that react-query can identify as a maintenance
 * error so it can suppress automatic retries.
 */
function createMaintenanceError(message) {
  const error = new Error(message || 'Service unavailable — maintenance in progress');
  error.isMaintenance = true;
  error.status = 503;
  return error;
}

/**
 * Routes for which unauthorized (401/403) error toasts should be suppressed.
 * These are polled/bootstrap endpoints where a failure shouldn't surface a
 * toast to the user.
 */
const SUPPRESS_ERROR_TOAST_ROUTES = ['companies', 'me'];

/**
 * shouldSuppressErrorToast
 *
 * Returns true when the response's URL path ends with one of the routes in
 * SUPPRESS_ERROR_TOAST_ROUTES (ignoring query string and trailing slash).
 *
 * @param {string} url
 * @returns {boolean}
 */
function shouldSuppressErrorToast(url) {
  if (!url) {
    return false;
  }

  let pathname = url;

  try {
    ({ pathname } = new URL(url));
  } catch (_err) {
    // url may be relative — strip the query string manually
    pathname = url.split('?')[0];
  }

  const lastSegment = pathname.replace(/\/+$/, '').split('/').pop();

  return SUPPRESS_ERROR_TOAST_ROUTES.includes(lastSegment);
}

/**
 * checkStatus
 *
 * handles various fetch http responses
 *
 * @param response
 * @returns {boolean|*}
 */
async function checkStatus(response) {
  if (response.status === 503) {
    let body = {};

    try {
      body = await response.clone().json();
    } catch (_err) {
      // ignore parse failures — body may not be JSON
    }

    if (body.error_code === 'MAINTENANCE_MODE') {
      handleMaintenanceResponse(body);
      throw createMaintenanceError(body.message);
    }
  }

  if (response.status === 401) {
    if (!shouldSuppressErrorToast(response.url)) {
      createToast({
        status: 'error',
        title: 'You are unauthorized to perform this action'
      });
    }

    const error = new Error('Unauthorized');
    error.status = 401;
    throw error;
  }

  if (response.status === 403) {
    if (!shouldSuppressErrorToast(response.url)) {
      createToast({
        status: 'error',
        title: 'You are unauthorized to perform this action'
      });
    }

    const error = new Error('Unauthorized');
    error.status = 403;
    throw error;
  }

  if (response.status >= 200 && response.status < 300) {
    handleSuccessfulResponsePostMaintenance();

    if (response.headers.get('content-type') === 'text/xml') {
      return true;
    }

    if (parseInt(response.headers.get('content-length'), 10) === 0 || response.status === 204) {
      return true;
    }

    try {
      return response.json();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Error in checkStatus:', err);
      return response.text();
    }
  }

  let errorMessage = 'Unknown error';

  try {
    errorMessage = (await response.json())?.message;
  } catch (err) {
    console.log('==============================================\n'); // eslint-disable-line no-console
    console.log('Error in checkStatus', err, '\n'); // eslint-disable-line no-console
    console.log('==============================================\n'); // eslint-disable-line no-console
  }

  if (typeof errorMessage === 'object') {
    const error = new Error(response.statusText);
    error.body = errorMessage;
    error.status = response.status;
    throw error;
  }

  const error = new Error(errorMessage || response.statusText);
  error.status = response.status;
  throw error;
}

/**
 * createBaseApi
 *
 * Constructs fetch methods with a base url and optional default query params & headers
 *
 * @param baseUrl
 * @param options
 * @returns {
 *   {
 *     putJson(*, *=, *=): Promise<boolean>,
 *     postJson(*, *=, *=): Promise<boolean>,
 *     deleteJson(*, *=, *=): Promise<boolean>,
 *     getJson(*, *=): Promise<boolean>
 *   } | Promise<boolean>
 * }
 */
export function createBaseApi(baseUrl, options = {}) {
  const { params: apiParams = {}, headers: apiHeaders = {} } = options;

  // `headers` may be passed as a plain object (evaluated once, back-compat)
  // or as a function that is invoked on every request so freshly-written
  // values (eg the auth token) are always read at call time, not import
  // time (DEV-3087).
  const resolveHeaders = () => (typeof apiHeaders === 'function' ? apiHeaders() : apiHeaders);

  return {
    getJson(url, ...params) {
      const fullUrl = url.charAt(0) === '/' ? baseUrl + url : `${baseUrl}/${url}`;
      const urlWithQueryDefaults = appendQuery(fullUrl, Object.assign({}, apiParams, ...params));

      return fetch(urlWithQueryDefaults, {
        method: 'GET',
        headers: { ...defaultHeaders, ...resolveHeaders() }
      }).then(checkStatus);
    },

    putJson(url, body = {}, ...params) {
      const fullUrl = url.charAt(0) === '/' ? baseUrl + url : `${baseUrl}/${url}`;
      const urlWithQueryDefaults = appendQuery(fullUrl, Object.assign(apiParams, ...params));

      return fetch(urlWithQueryDefaults, {
        method: 'PUT',
        headers: { ...defaultHeaders, ...resolveHeaders() },
        body: JSON.stringify(body)
      }).then(checkStatus);
    },

    postJson(url, body = {}, ...params) {
      const fullUrl = url.charAt(0) === '/' ? baseUrl + url : `${baseUrl}/${url}`;
      const urlWithQueryDefaults = appendQuery(fullUrl, Object.assign(apiParams, ...params));

      return fetch(urlWithQueryDefaults, {
        method: 'POST',
        headers: { ...defaultHeaders, ...resolveHeaders() },
        body: JSON.stringify(body)
      }).then(checkStatus);
    },

    deleteJson(url, body = {}, ...params) {
      const fullUrl = url.charAt(0) === '/' ? baseUrl + url : `${baseUrl}/${url}`;
      const urlWithQueryDefaults = appendQuery(fullUrl, Object.assign(apiParams, ...params));

      return fetch(urlWithQueryDefaults, {
        method: 'DELETE',
        headers: { ...defaultHeaders, ...resolveHeaders() },
        body: JSON.stringify(body)
      }).then(checkStatus);
    }
  };
}
